import React from 'react';
import { Facebook, Instagram, LinkedIn, YouTube } from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';
import { Col } from 'components/Core/Grid';
import SubscribeForm from 'components/Form/SubscribeForm';
import useGlobalFooter from 'hooks/useGlobalFooter';

import {
  BottomItem,
  Copyright,
  FooterBody,
  FooterBottom,
  FooterBottomMenu,
  FooterWrapper,
  IconItem,
  InnerContainer,
  Line,
  MenuItemLink,
  SocialIcons,
  STFooterLogo,
  Title,
  FormContainer,
} from './styles';

const WebsiteFooter = ({ anchorId, navigation }) => {
  const globalFooter = useGlobalFooter();
  const form = globalFooter?.form;

  if (!navigation) {
    navigation = globalFooter?.navigation?.navigation;
  }
  navigation
    .filter(
      (x) => x.label === 'Resources' && !x.subNavigationElement.some((y) => y.label === 'Pricing'),
    )
    .forEach((x) => {
      x.subNavigationElement.splice(0, 0, {
        label: 'Pricing',
        url: '/pricing',
        className: 'i-nav-link--pricing',
      });
    });

  const mobileMenuColumItems = navigation.reduce(
    (columns, item, index) => {
      const left = index % 2 === 0;
      left ? columns.left.push(item) : columns.right.push(item);
      return columns;
    },
    { left: [], right: [] },
  );

  const currentYear = new Date().getFullYear();

  const footer = {
    copyright: `Copyright @${currentYear}`,
    body: [
      {
        title: 'Privacy Policy',
        linkTo: '/legal/privacy-policy',
      },
      {
        title: 'Terms of Use',
        linkTo: '/legal/terms-of-use',
      },
      {
        title: 'Your Privacy Choices',
        linkTo: '/legal/privacy-request',
      },
      {
        component: (
          <div
            className="ot-cookie-setting-btn"
            onClick={() => window?.OneTrust?.ToggleInfoDisplay()}
          >
            <p className="font-p">Do Not Sell or Share My Personal Information</p>
          </div>
        ),
      },
    ],
  };

  const socialIcons = [
    {
      dataPosition: 'Footer - Facebook',
      url: 'https://www.facebook.com/ServiceTitan',
      icon: <Facebook />,
    },
    {
      dataPosition: 'Footer - Instagram',
      url: 'https://www.instagram.com/servicetitan/',
      icon: <Instagram />,
    },
    {
      dataPosition: 'Footer - X',
      url: 'https://twitter.com/ServiceTitan',
      icon: <XIcon />,
    },
    {
      dataPosition: 'Footer - LinkedIn',
      url: 'https://www.linkedin.com/company/servicetitan/',
      icon: <LinkedIn />,
    },
    {
      dataPosition: 'Footer - YouTube',
      url: 'https://www.youtube.com/user/servicetitan',
      icon: <YouTube />,
    },
  ];

  return (
    <FooterWrapper id={`section-${anchorId}`}>
      <InnerContainer>
        <STFooterLogo title="ServiceTitan" width="227" height="40" />
        <FooterBody className="max-md" gutter={{ xs: 20, md: 10 }}>
          {Object.keys(mobileMenuColumItems).map((column, index) => (
            <Col xs={12} md={12} key={`${index}--footerSection`}>
              {mobileMenuColumItems[column].map((column1, index1) => (
                <div
                  className={`footer--mobile-menu-column footer-${column1?.label?.toLowerCase()}-menu`}
                  key={`${index1}--footerSection-mobile`}
                >
                  <MenuLink item={column1} />
                </div>
              ))}
            </Col>
          ))}
        </FooterBody>
        <FooterBody className="min-md" gutter={{ xs: 20, md: 10 }}>
          {navigation.map((section, index) => (
            <Col
              xs={12}
              md={12}
              lg={12}
              key={`${index}--footerSection`}
              className={`footer-${section?.label?.toLowerCase()}-menu`}
            >
              <MenuLink item={section} />
            </Col>
          ))}
        </FooterBody>
        <Line className="block md:hidden" />
        <FooterBottom>
          <div className="footer-social-container w-full min-h-[158px]">
            <SocialIcons>
              {socialIcons.map((item, index) => {
                const { dataPosition, url, icon } = item;

                return (
                  <IconItem
                    data-position={dataPosition}
                    href={url}
                    rel="noreferrer"
                    target="_blank"
                    ctaPosition="footerbottomsociallink"
                    key={`social-icon-${index}`}
                  >
                    {icon}
                  </IconItem>
                );
              })}
            </SocialIcons>
            <FormContainer theme="light">
              <SubscribeForm
                inlineMarketingForm={form}
                buttonType="secondary"
                notificationMessage="Thanks for subscribing!"
                formType="Email Capture"
                dataPosition="Footer"
                label="Get our weekly newsletter"
                revealProps={{
                  styles: 'subscribe-form--footer-legal-info',
                }}
              />
            </FormContainer>
          </div>
          <Line className="hidden md:block" />
          <FooterBottomMenu>
            <Copyright className="footer--copyright" span={9}>
              {footer.copyright}
            </Copyright>
            <div className="footer-body-items">
              {footer.body.map((item, index) => {
                return (
                  item.component || (
                    <BottomItem
                      className="footer--bottom-item"
                      href={item.linkTo}
                      key={index}
                      ctaPosition="footerbottomlink"
                      target="_blank"
                    >
                      {item.title}
                    </BottomItem>
                  )
                );
              })}
            </div>
          </FooterBottomMenu>
        </FooterBottom>
      </InnerContainer>
    </FooterWrapper>
  );
};

export default WebsiteFooter;

const MenuLink = ({ item }) => {
  return (
    <>
      <Title className="footer--menu-item-title">{item.label}</Title>
      {item.subNavigationElement.map((menuItem, index) => (
        <MenuItemLink
          ctaPosition="footerlink"
          data-position="Footer - Nav Element"
          href={`${menuItem.url}`}
          key={`${index}--subtitle`}
          className={`${menuItem.className} font-p`}
        >
          {menuItem.label}
        </MenuItemLink>
      ))}
    </>
  );
};
